if (!window.Eurus || !window.Eurus.loadedScript || !window.Eurus.loadedScript.has('tsn-private-product-add-ons.js')) {
  if (window.Eurus && window.Eurus.loadedScript) {
    window.Eurus.loadedScript.add('tsn-private-product-add-ons.js');
  }

  window.TSNProductAddOns = window.TSNProductAddOns || {};

  (() => {
    const addonNamespace = window.TSNProductAddOns;
    let pendingFreeInners = [];
    const toNumber = (value, fallback = 0) => {
      const number = Number(value);
      return Number.isFinite(number) ? number : fallback;
    };

    const getForm = (root) => root.closest('form');

    const getQuantity = (form) => {
      const quantityInput = form?.querySelector('input[name="quantity"]');
      return Math.max(1, parseInt(quantityInput?.value || '1', 10) || 1);
    };

    const formatMoney = (cents, currencyCode) => {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
          style: 'currency',
          currency: currencyCode || 'GBP'
        }).format(cents / 100);
      } catch (error) {
        return `GBP ${(cents / 100).toFixed(2)}`;
      }
    };

    // Exact availability for the requested quantity is checked by the scoped
    // submit guard. Initial UI availability is a server-rendered boolean.
    const itemCanSupply = (item) => item.dataset.addonAvailable === 'true';

    const getAddonItems = (root) => {
      return Array.from(root.querySelectorAll('[data-tsn-addon-item]'));
    };

    const setPaymentButtonState = (form, hidePaymentButton) => {
      const paymentButton = form?.querySelector('.payment-button-container');
      if (!paymentButton) return;
      paymentButton.classList.toggle('hidden', hidePaymentButton);
    };

    const updateRoot = (root) => {
      const form = getForm(root);
      const checkbox = root.querySelector('[data-tsn-addon-checkbox]');
      const message = root.querySelector('[data-tsn-addon-message]');
      const price = root.querySelector('[data-tsn-addon-price]');
      const mainQuantity = getQuantity(form);
      const addonItems = getAddonItems(root);
      const baseTotalPrice = toNumber(root.dataset.baseTotalPrice, 0);
      const allAvailable = addonItems.every((item) => {
        const requiredQuantity = Math.max(1, toNumber(item.dataset.baseQuantity, 1)) * mainQuantity;
        return itemCanSupply(item, requiredQuantity);
      });

      if (price) {
        price.textContent = root.dataset.freeInnerOffer === 'true'
          ? 'Free'
          : `+ ${formatMoney(baseTotalPrice * mainQuantity, root.dataset.currencyCode)}`;
      }

      if (checkbox) {
        checkbox.disabled = !allAvailable;
        if (!allAvailable) checkbox.checked = false;
      }

      root.classList.toggle('is-unavailable', !allAvailable);

      if (message) {
        message.textContent = allAvailable ? '' : (
          mainQuantity > 1 ? root.dataset.insufficientMessage : root.dataset.unavailableMessage
        );
        message.classList.toggle('hidden', allAvailable);
      }

      const isSelected = Boolean(checkbox?.checked && allAvailable);
      setPaymentButtonState(form, isSelected);
      return isSelected;
    };

    const initRoot = (root) => {
      if (root.dataset.tsnAddonInitialized === 'true') return;
      root.dataset.tsnAddonInitialized = 'true';

      const form = getForm(root);
      const checkbox = root.querySelector('[data-tsn-addon-checkbox]');
      const sectionId = root.dataset.sectionId;
      const variantInput = form?.querySelector('input[name="id"]');
      const quantityInput = form?.querySelector('input[name="quantity"]');

      const refreshSoon = () => window.requestAnimationFrame(() => updateRoot(root));

      checkbox?.addEventListener('change', refreshSoon);
      variantInput?.addEventListener('change', refreshSoon);
      quantityInput?.addEventListener('input', refreshSoon);
      quantityInput?.addEventListener('change', refreshSoon);
      form?.addEventListener('click', refreshSoon);

      if (sectionId) {
        document.addEventListener(`eurus:product:quantity-changed-${sectionId}`, refreshSoon);
      }

      updateRoot(root);
    };

    const initProductAddOns = () => {
      document.querySelectorAll('[data-tsn-product-add-ons]').forEach(initRoot);
    };

    addonNamespace.prepareForm = (form) => {
      const root = form?.querySelector('[data-tsn-product-add-ons]');
      if (!root) return false;
      return updateRoot(root);
    };

    addonNamespace.hasSelectedAddOns = (form) => {
      const root = form?.querySelector('[data-tsn-product-add-ons]');
      if (!root) return false;
      return updateRoot(root);
    };

    addonNamespace.mainProductHasComponents = (form) => {
      const root = form?.querySelector('[data-tsn-product-add-ons]');
      return root?.dataset.productHasComponents === 'true';
    };

    addonNamespace.getSelectedItems = (form, options = {}) => {
      const root = form?.querySelector('[data-tsn-product-add-ons]');
      pendingFreeInners = [];
      if (window.Alpine?.store('xCartNoti')) {
        window.Alpine.store('xCartNoti').freeInnerItems = [];
      }
      if (!root || !updateRoot(root)) return [];

      const mainQuantity = getQuantity(form);
      const parentVariantId = options.parentVariantId ? String(options.parentVariantId) : '';
      const parentTitle = typeof options.parentTitle === 'string' ? options.parentTitle.trim() : '';
      const useParentId = options.useParentId === true;

      const selectedItems = getAddonItems(root).map((item) => {
        const baseQuantity = Math.max(1, toNumber(item.dataset.baseQuantity, 1));
        const quantity = baseQuantity * mainQuantity;

        if (!itemCanSupply(item, quantity)) return null;

        const selectedItem = {
          id: item.dataset.variantId,
          quantity
        };
        // Use Shopify's native relationship for PERFECTFIT, never printable
        // line-item properties. Ordinary add-ons need no parent metadata.
        const requiresDiscountLink = parentTitle.includes('Cushion Cover')
          && !parentTitle.includes('Cushion Inner')
          && (item.dataset.productTitle || '').startsWith('Duck Feather Filled Cushion Inner');
        if (requiresDiscountLink && parentVariantId && useParentId) {
          selectedItem.parent_id = parentVariantId;
        }

        return selectedItem;
      }).filter(Boolean);

      if (root.dataset.freeInnerOffer === 'true') {
        pendingFreeInners = getAddonItems(root).map((item, index) => ({
          id: Number(item.dataset.variantId),
          variant_id: Number(item.dataset.variantId),
          title: item.dataset.productTitle,
          quantity: selectedItems[index]?.quantity || 0,
          price: 0,
          url: item.dataset.productUrl,
          productUrl: item.dataset.productUrl,
          featured_image: { url: item.dataset.productImage || '' }
        })).filter((item) => item.quantity > 0);
      }

      if (window.Alpine?.store('xCartNoti')) {
        window.Alpine.store('xCartNoti').freeInnerItems = pendingFreeInners;
      }

      return selectedItems;
    };

    document.addEventListener('eurus:product:added', () => {
      pendingFreeInners = [];
    });
    document.addEventListener('eurus:product:add-failed', () => {
      pendingFreeInners = [];
      if (window.Alpine?.store('xCartNoti')) {
        window.Alpine.store('xCartNoti').freeInnerItems = [];
      }
    });

    document.addEventListener('DOMContentLoaded', initProductAddOns);
    document.addEventListener('shopify:section:load', initProductAddOns);

    if (document.readyState !== 'loading') {
      initProductAddOns();
    }
  })();
}
